/**
 * Opening a source document in a new tab, without failing silently.
 *
 * A document reference used to navigate a tab straight at the presigned URL.
 * When that navigation fails there is no error handler anywhere: the tab shows
 * the browser's own error page and we learn nothing. EXTENSION-BW was exactly
 * that — a Kramer user saw `ERR_BLOCKED_BY_CLIENT` (an endpoint blocker on
 * their machine, the proxy logged 200 OK for every request) and the only
 * evidence we ever got was the sentence they typed into the feedback widget.
 *
 * So proxy-served URLs are resolved with `fetch()` first, exactly as Venus
 * already does before handing one to its viewer iframe (see
 * DocumentsPage/MessagingSection, #507): a failure becomes a German toast plus
 * a Sentry event, and the tab only ever navigates to bytes we actually hold.
 *
 * GCS presigned URLs are cross-origin and cannot be fetched, so they keep
 * navigating directly — unchanged, and still invisible on failure.
 */

import * as Sentry from "@sentry/react";
import { toast } from "sonner";

import { isProxyServedUrl } from "./proxyUrls";

type TFunction = (key: string) => string;

export interface OpenDocumentOptions {
    /** Presigned or proxy URL, WITHOUT the page fragment. */
    url: string;
    /** e.g. `#page=4`. Appended after the URL is resolved, never before. */
    pageFragment?: string;
    /**
     * Tab opened synchronously inside the click handler. Opening it before the
     * first `await` is what keeps popup blockers out of this; a null tab (the
     * blocker won anyway) falls back to a late `window.open`.
     */
    tab: Window | null;
    /** Tagged onto the Sentry event so a report names the document. */
    documentId?: string | null;
    /** Name for the saved file when the browser cannot render the type inline. */
    filename?: string | null;
    t: TFunction;
}

/**
 * Object URLs stay alive until revoked, and Neptune's surface is a long-lived
 * iframe — a shift of opening manuals would otherwise retain every PDF for the
 * session. Keeping the last few means a reload of an older tab can fail, which
 * is a far smaller problem than holding hundreds of megabytes.
 */
const MAX_RETAINED_BLOB_URLS = 8;
const retainedBlobUrls: string[] = [];

function retainBlobUrl(objectUrl: string): void {
    retainedBlobUrls.push(objectUrl);
    while (retainedBlobUrls.length > MAX_RETAINED_BLOB_URLS) {
        URL.revokeObjectURL(retainedBlobUrls.shift()!);
    }
}

/** Types a browser tab shows itself; everything else (docx, xlsx, ...) is saved. */
function isInlineRenderable(contentType: string): boolean {
    return (
        contentType === "application/pdf" ||
        contentType.startsWith("image/") ||
        contentType.startsWith("text/")
    );
}

/**
 * Original filename from the proxy's `Content-Disposition`
 * (`filename*=UTF-8''...` preferred, plain `filename="..."` as fallback).
 */
export function filenameFromContentDisposition(header: string | null): string | null {
    if (!header) return null;
    const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (encoded) {
        try {
            return decodeURIComponent(encoded[1].trim());
        } catch {
            // Malformed escape — fall through to the plain parameter.
        }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return plain ? plain[1].trim() : null;
}

function saveAs(objectUrl: string, filename: string): void {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
}

function navigate(tab: Window | null, url: string): void {
    if (tab && !tab.closed) {
        tab.location.href = url;
        return;
    }
    window.open(url, "_blank");
}

function closeTab(tab: Window | null): void {
    // Leaving the blank tab we opened behind would read as "it opened, but
    // empty" — the very confusion the toast is meant to replace.
    if (tab && !tab.closed) tab.close();
}

/**
 * Fill the blank tab while the document downloads. A service manual is tens of
 * megabytes, and an untouched white tab looks like the failure we are trying to
 * report. Best-effort: an embedder that denies document access just keeps the
 * blank tab.
 */
function writeLoadingPlaceholder(tab: Window | null, message: string): void {
    if (!tab || tab.closed) return;
    try {
        tab.document.write(
            `<!doctype html><meta charset="utf-8"><title>${message}</title>` +
                `<body style="margin:0;display:grid;place-items:center;height:100vh;` +
                `font:400 14px/1.6 Sora,system-ui,sans-serif;color:#07195F">${message}</body>`,
        );
        tab.document.close();
    } catch {
        // Cross-origin or sandboxed opener — the tab stays blank, which is what
        // it did before this placeholder existed.
    }
}

/**
 * Resolve a document URL and show it in `tab`, toasting instead of navigating
 * when it cannot be loaded.
 */
export async function openDocument({
    url,
    pageFragment = "",
    tab,
    documentId,
    filename,
    t,
}: OpenDocumentOptions): Promise<void> {
    if (!isProxyServedUrl(url)) {
        navigate(tab, `${url}${pageFragment}`);
        return;
    }

    writeLoadingPlaceholder(tab, t("messageReferences.openLoading"));

    let objectUrl: string;
    let blobType = "";
    let savedName: string | null = null;
    try {
        const response = await fetch(url);
        if (!response.ok) {
            closeTab(tab);
            let detail: string | undefined;
            try {
                detail = ((await response.json()) as { detail?: string })?.detail;
            } catch {
                // Body was the gateway's HTML error page, not the proxy's JSON.
            }
            Sentry.captureMessage("document_open_failed", {
                level: "error",
                tags: { document_open_failure: "http" },
                extra: { status: response.status, documentId, detail },
            });
            toast.error(t("messageReferences.openError"), {
                description: detail ?? t("messageReferences.openErrorUnavailable"),
            });
            return;
        }
        const blob = await response.blob();
        blobType = (blob.type || response.headers.get("Content-Type") || "").toLowerCase();
        savedName =
            filename ?? filenameFromContentDisposition(response.headers.get("Content-Disposition"));
        objectUrl = URL.createObjectURL(blob);
    } catch (error) {
        closeTab(tab);
        // A request the browser refuses to send never reaches the proxy, so
        // this handler is the only record it leaves anywhere (EXTENSION-BW).
        Sentry.captureException(error, {
            tags: { document_open_failure: "network" },
            extra: { documentId },
        });
        toast.error(t("messageReferences.openError"), {
            description: t("messageReferences.openErrorBlocked"),
        });
        return;
    }

    retainBlobUrl(objectUrl);
    if (savedName && !isInlineRenderable(blobType)) {
        // A tab pointed at a blob: URL the browser cannot render downloads it
        // under the blob's random UUID. An anchor with `download` keeps the name.
        closeTab(tab);
        saveAs(objectUrl, savedName);
        return;
    }
    navigate(tab, `${objectUrl}${pageFragment}`);
}

interface ClickModifiers {
    defaultPrevented: boolean;
    button: number;
    metaKey: boolean;
    ctrlKey: boolean;
    shiftKey: boolean;
    altKey: boolean;
}

/**
 * Whether a click on a document anchor should be handled by {@link openDocument}
 * instead of the browser.
 *
 * Modified clicks (new window, background tab, download) and non-proxy URLs
 * keep the plain-navigation behaviour the anchor exists for, so "open in new
 * tab" and "copy link" still do what they say.
 */
export function shouldInterceptDocumentClick(event: ClickModifiers, url: string): boolean {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    return isProxyServedUrl(url);
}

/**
 * Click handler for a document anchor: takes over a plain left click, opens the
 * tab inside the gesture, and hands the URL to {@link openDocument}.
 *
 * The anchor keeps its real `href`, so anything the handler declines — a
 * middle click, cmd-click, "copy link address" — navigates as before.
 */
export function handleDocumentAnchorClick(
    event: ClickModifiers & { preventDefault: () => void },
    options: Omit<OpenDocumentOptions, "tab">,
): void {
    if (!shouldInterceptDocumentClick(event, options.url)) return;
    event.preventDefault();
    void openDocument({ ...options, tab: window.open("", "_blank") });
}
